import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { sharedStyles, buttonStyles, cardStyles, badgeStyles } from '../styles/shared.js';
import {
  cartState,
  formatPrice,
  sanitizeCustomerFacingCopy,
  type Product,
  type ProductVariant,
} from '@landi/store-core';
import './landi-product-image.js';

/**
 * Product card component for displaying products in grids/lists
 * Styled to match The Golden Peptides light theme
 * 
 * @element landi-product-card
 * @fires product-click - Fired when product is clicked
 * @fires add-to-cart - Fired when add to cart is clicked
 */
@customElement('landi-product-card')
export class LandiProductCard extends LitElement {
  static styles = [
    sharedStyles,
    buttonStyles,
    cardStyles,
    badgeStyles,
    css`
      :host {
        display: block;
      }
      
      .product-card {
        cursor: pointer;
        height: 100%;
        display: flex;
        flex-direction: column;
        background: var(--landi-surface);
        border-radius: var(--landi-radius);
        overflow: hidden;
        transition: box-shadow 300ms ease, transform 300ms ease;
      }
      
      .product-card:hover {
        box-shadow: var(--landi-shadow-lg);
        transform: translateY(-2px);
      }
      
      .product-image-wrapper {
        position: relative;
        overflow: hidden;
        aspect-ratio: 4/3;
        background: var(--landi-background);
      }
      
      .product-image {
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition: transform 700ms ease;
      }
      
      .product-card:hover .product-image {
        transform: scale(1.1);
      }
      
      .product-badges {
        position: absolute;
        top: 0.75rem;
        left: 0.75rem;
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }
      
      .product-content {
        padding: 1.25rem;
        flex: 1;
        display: flex;
        flex-direction: column;
      }
      
      .product-name {
        font-family: var(--landi-font-serif);
        font-size: 1.25rem;
        font-weight: 500;
        margin: 0 0 0.5rem;
        color: var(--landi-text);
        line-height: 1.3;
        transition: color 200ms ease;
      }
      
      .product-card:hover .product-name {
        color: var(--landi-primary);
      }
      
      .product-description {
        font-size: 0.875rem;
        color: var(--landi-text-muted);
        margin: 0 0 0.75rem;
        line-height: 1.6;
        font-weight: 300;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }
      
      .product-specs {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem 1rem;
        margin-bottom: 0.75rem;
        padding: 0.625rem 0.75rem;
        background: var(--landi-background);
        border-radius: var(--landi-radius);
      }
      
      .spec-item {
        display: flex;
        align-items: center;
        gap: 0.375rem;
        font-size: 0.75rem;
      }
      
      .spec-label {
        color: var(--landi-text-muted);
        font-weight: 400;
      }
      
      .spec-value {
        color: var(--landi-text);
        font-weight: 500;
      }
      
      .spec-value.purity {
        color: var(--landi-success, #10b981);
      }
      
      .product-rating {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-size: 0.813rem;
        color: var(--landi-text-muted);
        margin-bottom: 0.75rem;
      }
      
      .stars {
        color: var(--landi-primary);
        letter-spacing: 2px;
      }
      
      .product-footer {
        margin-top: auto;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.75rem;
        padding-top: 0.875rem;
        border-top: 1px solid var(--landi-border-light);
      }
      
      .product-price-row {
        display: flex;
        align-items: baseline;
        gap: 0.5rem;
        flex-wrap: wrap;
      }
      
      .price-current {
        font-size: 1.125rem;
        font-weight: 700;
        color: var(--landi-text);
      }
      
      .price-quantity {
        font-size: 0.75rem;
        color: var(--landi-text-muted);
        font-weight: 400;
      }
      
      .price-compare {
        font-size: 0.813rem;
        color: var(--landi-text-light);
        text-decoration: line-through;
      }
      
      .btn-add {
        flex-shrink: 0;
        background: var(--landi-text);
        color: var(--landi-surface);
        padding: 0.625rem 1rem;
        font-size: 0.688rem;
      }
      
      .btn-add:hover {
        background: var(--landi-primary);
      }
      
      .out-of-stock {
        color: var(--landi-error);
        font-size: 0.75rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }
    `,
  ];

  @property({ type: String, attribute: 'product-id' }) productId = '';
  @property({ type: String }) name = '';
  @property({ type: Number }) price = 0;
  @property({ type: Number, attribute: 'compare-price' }) comparePrice?: number;
  @property({ type: String }) image = '';
  @property({ type: String }) sku = '';
  @property({ type: String }) category = '';
  @property({ type: String }) description = '';
  @property({ type: Number }) rating = 0;
  @property({ type: Number }) reviews = 0;
  @property({ type: Boolean, attribute: 'in-stock' }) inStock = true;
  @property({ type: Boolean }) featured = false;
  @property({ type: String }) href = '';
  @property({ type: String }) format = '';
  @property({ type: String }) purity = '';
  @property({ type: String, attribute: 'test-method' }) testMethod = '';
  @property({ type: String }) quantity = '';
  @property({ attribute: false }) product?: Product;

  private resolveDefaultVariant(product: Product): ProductVariant | undefined {
    if (product.variants.length === 0) {
      return undefined;
    }

    const inStockVariant = product.variants.find(
      (variant) => variant.inStock || variant.inventoryQuantity > 0,
    );
    return inStockVariant ?? product.variants[0];
  }

  private handleClick(e: Event) {
    // Don't navigate if clicking the add to cart button
    if ((e.target as HTMLElement).closest('.btn-add')) return;
    
    this.dispatchEvent(new CustomEvent('product-click', {
      bubbles: true,
      composed: true,
      detail: { productId: this.productId },
    }));
    
    if (this.href) {
      window.location.href = this.href;
    }
  }

  private handleAddToCart(e: Event) {
    e.stopPropagation();

    const variant = this.product ? this.resolveDefaultVariant(this.product) : undefined;

    if (this.product) {
      if (this.product.variants.length > 0 && !variant) {
        return;
      }

      cartState.addItem(this.product, variant, 1);
    }

    this.dispatchEvent(new CustomEvent('add-to-cart', {
      bubbles: true,
      composed: true,
      detail: {
        productId: this.productId,
        variantId: variant?.id,
        sku: variant?.sku ?? this.product?.sku ?? this.sku,
        quantity: 1,
      },
    }));
  }

  private renderStars(rating: number) {
    const fullStars = Math.floor(rating);
    const hasHalf = rating % 1 >= 0.5;
    const stars = [];
    
    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push('★');
      } else if (i === fullStars && hasHalf) {
        stars.push('★'); // Could use a half star icon
      } else {
        stars.push('☆');
      }
    }
    
    return stars.join('');
  }

  render() {
    return html`
      <article class="card product-card" @click=${this.handleClick}>
        <div class="product-image-wrapper">
          <landi-product-image
            img-class="product-image"
            image-url=${this.image}
            product-name=${this.name}
            product-sku=${this.sku}
            product-category=${this.category}
            theme="light"
            alt=${this.name}
            loading="lazy"
          ></landi-product-image>
          <div class="product-badges">
            ${this.featured ? html`<span class="badge badge-primary">Featured</span>` : ''}
            ${!this.inStock ? html`<span class="badge badge-error">Out of Stock</span>` : ''}
            ${this.comparePrice && this.comparePrice > this.price 
              ? html`<span class="badge badge-success">Sale</span>` 
              : ''}
          </div>
        </div>
        
        <div class="product-content">
          <h3 class="product-name">${this.name}</h3>
          
          ${this.description ? html`
            <p class="product-description">${sanitizeCustomerFacingCopy(this.description)}</p>
          ` : ''}
          
          ${this.format || this.purity || this.testMethod ? html`
            <div class="product-specs">
              ${this.format ? html`
                <span class="spec-item">
                  <span class="spec-label">Format:</span>
                  <span class="spec-value">${this.format}</span>
                </span>
              ` : ''}
              ${this.purity ? html`
                <span class="spec-item">
                  <span class="spec-label">Purity:</span>
                  <span class="spec-value purity">${this.purity}</span>
                </span>
              ` : ''}
              ${this.testMethod ? html`
                <span class="spec-item">
                  <span class="spec-label">Method:</span>
                  <span class="spec-value">${this.testMethod}</span>
                </span>
              ` : ''}
            </div>
          ` : ''}
          
          ${this.rating > 0 ? html`
            <div class="product-rating">
              <span class="stars">${this.renderStars(this.rating)}</span>
              <span>(${this.reviews} reviews)</span>
            </div>
          ` : ''}
          
          <div class="product-footer">
            <div class="product-price-row">
              <span class="price-current">${formatPrice(this.price)}</span>
              ${this.quantity ? html`<span class="price-quantity">${this.quantity}</span>` : ''}
              ${this.comparePrice && this.comparePrice > this.price ? html`
                <span class="price-compare">${formatPrice(this.comparePrice)}</span>
              ` : ''}
            </div>
            
            ${this.inStock ? html`
              <button 
                class="btn btn-primary btn-sm btn-add" 
                @click=${this.handleAddToCart}
              >
                Add to Cart
              </button>
            ` : html`
              <span class="out-of-stock">Out of Stock</span>
            `}
          </div>
        </div>
      </article>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'landi-product-card': LandiProductCard;
  }
}
